'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

interface RevealOnScrollProps {
  children: ReactNode;
  /** Delay en ms entre que entra al viewport y se revela (para stagger). Default: 0 */
  delay?: number;
  /** Distancia inicial en px (translateY). Default: 24 */
  offset?: number;
  /** Duración de la animación en ms. Default: 600 */
  duration?: number;
  /** Si se pasa, una vez revelado se queda revelado (default true). false = se oculta al salir */
  once?: boolean;
  /** className adicional para el wrapper */
  className?: string;
  /** Tag HTML del wrapper. Default: "div" */
  as?: 'div' | 'section' | 'article';
}

/**
 * Wrapper que aplica fade-up cuando el elemento entra al viewport.
 * Usa IntersectionObserver (no scroll listener) → GPU-friendly, no jank.
 * Respeta prefers-reduced-motion: 0 transición, todo visible inmediatamente.
 */
export default function RevealOnScroll({
  children,
  delay = 0,
  offset = 24,
  duration = 600,
  once = true,
  className = '',
  as: Tag = 'div',
}: RevealOnScrollProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (reducedMotion) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setRevealed(false);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, reducedMotion]);

  const style: React.CSSProperties = reducedMotion
    ? {}
    : {
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'translate3d(0,0,0)' : `translate3d(0, ${offset}px, 0)`,
        transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: revealed ? 'auto' : 'opacity, transform',
      };

  return (
    <Tag ref={ref as React.RefObject<HTMLDivElement>} className={className} style={style}>
      {children}
    </Tag>
  );
}
